import React, { useEffect, useMemo, useState } from "react";
import { Download, Search, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { useAdminAuth, API_BASE } from "@/admin/AdminAuthContext";
import { downloadCsv, fmtDate } from "@/admin/utils";

const AdminLeads = () => {
  const { authedAxios } = useAdminAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    authedAxios
      .get(`${API_BASE}/leads?limit=500`)
      .then((r) => setLeads(r.data || []))
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return leads;
    const t = q.trim().toLowerCase();
    return leads.filter((l) =>
      [l.name, l.email, l.phone, l.interest, l.advisor, l.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(t))
    );
  }, [leads, q]);

  const exportCsv = () => {
    downloadCsv(
      `emb-leads-${new Date().toISOString().slice(0, 10)}.csv`,
      filtered,
      [
        { label: "Fecha", value: (r) => fmtDate(r.created_at) },
        { label: "Nombre", value: (r) => r.name },
        { label: "Email", value: (r) => r.email },
        { label: "Teléfono", value: (r) => r.phone },
        { label: "Interés", value: (r) => r.interest },
        { label: "Asesor solicitado", value: (r) => r.advisor || "" },
        { label: "Mensaje", value: (r) => r.message || "" },
      ]
    );
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-leads-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mb-2">
            Leads · Solicitudes de asesoría
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-light leading-tight">
            <span className="italic text-[#E6D5B8]">{leads.length}</span> leads
            registrados
          </h1>
        </div>
        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          data-testid="leads-export-btn"
          className="inline-flex items-center gap-2 bg-[#E6D5B8] hover:bg-[#FBEFD3] disabled:opacity-50 disabled:cursor-not-allowed text-[#0B0410] px-5 py-3 text-xs tracking-luxury uppercase font-semibold rounded-sm transition-colors"
        >
          <Download size={14} />
          <span>Exportar CSV</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search
          size={14}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A397B4]"
        />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre, email, interés o asesor..."
          data-testid="leads-search"
          className="w-full pl-11 pr-4 py-3 bg-[#150A1F] border border-[#2A0A4A] focus:border-[#E6D5B8] outline-none rounded-sm text-sm text-[#F9F6F0] placeholder:text-[#A397B4]/55"
        />
      </div>

      {loading && (
        <div className="text-sm text-[#F9F6F0]/50 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando...
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="bg-[#150A1F] border border-[#2A0A4A] rounded-sm p-10 text-center text-sm text-[#F9F6F0]/55">
          {leads.length === 0
            ? "Aún no hay leads recibidos."
            : "Ningún lead coincide con tu búsqueda."}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="bg-[#150A1F] border border-[#2A0A4A] rounded-sm overflow-hidden">
          {/* Table header */}
          <div className="hidden md:grid grid-cols-[1.6fr_1.6fr_1.2fr_1.2fr_120px] text-[10px] tracking-luxury uppercase text-[#A397B4] px-5 py-3 border-b border-white/10 bg-white/[0.02]">
            <div>Nombre</div>
            <div>Email</div>
            <div>Interés</div>
            <div>Teléfono</div>
            <div className="text-right">Fecha</div>
          </div>

          <div className="divide-y divide-white/[0.07]">
            {filtered.map((l) => {
              const isOpen = expanded === l.id;
              return (
                <div
                  key={l.id}
                  data-testid={`lead-row-${l.id}`}
                  className={`transition-colors ${isOpen ? "bg-white/[0.03]" : "hover:bg-white/[0.02]"}`}
                >
                  <button
                    onClick={() => setExpanded(isOpen ? null : l.id)}
                    className="w-full text-left grid grid-cols-1 md:grid-cols-[1.6fr_1.6fr_1.2fr_1.2fr_120px] gap-y-1 gap-x-4 px-5 py-4 items-center"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isOpen ? (
                        <ChevronDown size={14} className="text-[#E6D5B8] shrink-0" />
                      ) : (
                        <ChevronRight size={14} className="text-[#A397B4] shrink-0" />
                      )}
                      <span className="font-display text-base truncate">
                        {l.name}
                      </span>
                    </div>
                    <a
                      href={`mailto:${l.email}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm text-[#F9F6F0]/75 hover:text-[#E6D5B8] truncate"
                    >
                      {l.email}
                    </a>
                    <div className="text-xs text-[#F9F6F0]/65">
                      {l.interest}
                    </div>
                    <a
                      href={`tel:${l.phone}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-[#F9F6F0]/75 hover:text-[#E6D5B8]"
                    >
                      {l.phone}
                    </a>
                    <div className="text-[10px] tracking-luxury uppercase text-[#A397B4] md:text-right">
                      {fmtDate(l.created_at)}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 pl-12 grid sm:grid-cols-2 gap-5 border-t border-white/5 pt-4">
                      {l.advisor && (
                        <div>
                          <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-1">
                            Asesor solicitado
                          </div>
                          <div className="text-sm">{l.advisor}</div>
                        </div>
                      )}
                      <div className="sm:col-span-2">
                        <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-1">
                          Mensaje
                        </div>
                        <div className="text-sm text-[#F9F6F0]/80 whitespace-pre-wrap font-light leading-relaxed">
                          {l.message || (
                            <em className="text-[#F9F6F0]/40">
                              Sin mensaje adicional.
                            </em>
                          )}
                        </div>
                      </div>
                      <div className="sm:col-span-2 pt-2 flex gap-3 flex-wrap">
                        <a
                          href={`mailto:${l.email}`}
                          className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] border border-[#E6D5B8]/30 hover:border-[#E6D5B8] px-3 py-1.5 rounded-sm"
                        >
                          Responder por email
                        </a>
                        <a
                          href={`https://wa.me/${l.phone.replace(/\D/g, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] border border-[#E6D5B8]/30 hover:border-[#E6D5B8] px-3 py-1.5 rounded-sm"
                        >
                          WhatsApp
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLeads;
